/**
 * Reflection API
 *
 * Mock reflection service for post-challenge reflections.
 * Based on Doc 7: Reflection & SEL Tracking (Section 3.2 only for MVP)
 */

import type {
  ReflectionPrompt,
  ReflectionResponse,
  ReflectionAnalytics,
  SchoolQuestionBankPrompt,
  ApiResponse,
  UUID,
} from '../types'
import { mockDb, delay, timestamp } from './mockDb'
import { success, error, safeExecute } from './client'

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

// Default reflection prompts for MVP (platform-level)
const DEFAULT_PROMPTS: Omit<ReflectionPrompt, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    question: 'How did this challenge make you feel?',
    type: 'emoji_scale',
    options: ['😊', '🙂', '😐', '😕', '😢'],
    selCompetency: 'self_awareness',
    ageGroup: 'all',
    isRequired: true,
    displayOrder: 1,
  },
  {
    question: 'What was the hardest part of this challenge?',
    type: 'open_text_short',
    selCompetency: 'self_awareness',
    ageGroup: 'all',
    isRequired: true,
    displayOrder: 2,
  },
  {
    question: 'Did you learn something new?',
    type: 'multiple_choice_single',
    options: ['Yes, a lot!', 'A little bit', 'Not really', 'I already knew this'],
    selCompetency: 'self_awareness',
    ageGroup: 'all',
    isRequired: true,
    displayOrder: 3,
  },
  {
    question: 'Would you recommend this challenge to a friend?',
    type: 'multiple_choice_single',
    options: ['Definitely!', 'Maybe', 'Probably not', 'No'],
    selCompetency: 'relationship_skills',
    ageGroup: 'all',
    isRequired: false,
    displayOrder: 4,
  },
  {
    question: 'What would you do differently next time?',
    type: 'open_text_long',
    selCompetency: 'responsible_decision_making',
    ageGroup: '13_plus',
    isRequired: false,
    displayOrder: 5,
  },
]

// School-specific prompts (keyed by schoolId)
const SCHOOL_PROMPTS: Record<string, Omit<ReflectionPrompt, 'id' | 'createdAt' | 'updatedAt'>[]> = {
  'school-001': [
    {
      question: 'How did this challenge connect to what you are learning in class?',
      type: 'open_text_short',
      selCompetency: 'self_awareness',
      ageGroup: 'all',
      isRequired: false,
      displayOrder: 6,
    },
    {
      question: 'Did you collaborate with classmates on this challenge?',
      type: 'multiple_choice_single',
      options: ['Yes, we worked as a team', 'A little bit', 'No, I did it alone'],
      selCompetency: 'relationship_skills',
      ageGroup: 'all',
      isRequired: false,
      displayOrder: 7,
    },
  ],
  'school-002': [
    {
      question: 'How does this challenge relate to our school values?',
      type: 'open_text_long',
      selCompetency: 'social_awareness',
      ageGroup: 'all',
      isRequired: false,
      displayOrder: 6,
    },
  ],
}

// Assignment-specific prompts (keyed by assignmentId)
const ASSIGNMENT_PROMPTS: Record<string, Omit<ReflectionPrompt, 'id' | 'createdAt' | 'updatedAt'>[]> = {
  'assignment-001': [
    {
      question: 'How did this assignment challenge your problem-solving skills?',
      type: 'open_text_short',
      selCompetency: 'responsible_decision_making',
      ageGroup: 'all',
      isRequired: true,
      displayOrder: 8,
    },
    {
      // Overrides a platform default by same question text (deduplication test)
      question: 'What was the hardest part of this challenge?',
      type: 'open_text_long', // Upgraded to long text for this assignment
      selCompetency: 'self_management',
      ageGroup: 'all',
      isRequired: true,
      displayOrder: 2,
    },
  ],
  'assignment-002': [
    {
      question: 'What resources did your teacher provide that helped you?',
      type: 'open_text_short',
      selCompetency: 'social_awareness',
      ageGroup: 'all',
      isRequired: false,
      displayOrder: 9,
    },
  ],
}

/**
 * Deduplicate prompts by question text, keeping the most specific version.
 * Later entries (more specific) override earlier entries (less specific).
 */
function deduplicatePrompts(
  prompts: Omit<ReflectionPrompt, 'id' | 'createdAt' | 'updatedAt'>[]
): Omit<ReflectionPrompt, 'id' | 'createdAt' | 'updatedAt'>[] {
  const seen = new Map<string, Omit<ReflectionPrompt, 'id' | 'createdAt' | 'updatedAt'>>()

  for (const prompt of prompts) {
    // Normalize question text for comparison (lowercase, trimmed)
    const key = prompt.question.toLowerCase().trim()
    seen.set(key, prompt)
  }

  return Array.from(seen.values())
}

/**
 * Reflection API
 */
export const reflectionApi = {
  /**
   * Get reflection prompts for a track record
   * Filters by user's age group.
   * Supports prompt layering: platform defaults -> school-specific -> assignment-specific.
   * Deduplicates by question text, keeping the most specific version.
   */
  async getPrompts(
    isUnder13: boolean,
    schoolId?: string,
    assignmentId?: string
  ): Promise<ApiResponse<ReflectionPrompt[]>> {
    await delay(50)

    // Ensure platform default prompts are seeded into mockDb
    const existingPrompts = mockDb.getAll<ReflectionPrompt>('reflectionPrompts')
    if (existingPrompts.length === 0) {
      for (const prompt of DEFAULT_PROMPTS) {
        mockDb.insert<ReflectionPrompt>('reflectionPrompts', prompt as ReflectionPrompt)
      }
    }

    // Start with platform defaults
    const layeredPrompts: Omit<ReflectionPrompt, 'id' | 'createdAt' | 'updatedAt'>[] = [
      ...DEFAULT_PROMPTS,
    ]

    // Layer school-specific prompts if schoolId provided
    if (schoolId && SCHOOL_PROMPTS[schoolId]) {
      layeredPrompts.push(...SCHOOL_PROMPTS[schoolId])
    }

    // Layer assignment-specific prompts if assignmentId provided
    if (assignmentId && ASSIGNMENT_PROMPTS[assignmentId]) {
      layeredPrompts.push(...ASSIGNMENT_PROMPTS[assignmentId])
    }

    // Deduplicate by question text (most specific version wins)
    const deduplicated = deduplicatePrompts(layeredPrompts)

    // Filter by age group
    const ageGroup = isUnder13 ? 'under_13' : '13_plus'
    const filteredPrompts = deduplicated.filter(
      (p) => p.ageGroup === 'all' || p.ageGroup === ageGroup
    )

    // Sort by display order
    filteredPrompts.sort((a, b) => a.displayOrder - b.displayOrder)

    // Assign stable IDs from mockDb or generate ephemeral ones
    const dbPrompts = mockDb.getAll<ReflectionPrompt>('reflectionPrompts')
    const result: ReflectionPrompt[] = filteredPrompts.map((p) => {
      // Try to match by question text to an existing DB record
      const existing = dbPrompts.find(
        (db) => db.question.toLowerCase().trim() === p.question.toLowerCase().trim()
      )
      if (existing) {
        return { ...existing, ...p, id: existing.id, createdAt: existing.createdAt, updatedAt: existing.updatedAt }
      }
      // Insert into mockDb for a stable ID
      const inserted = mockDb.insert<ReflectionPrompt>('reflectionPrompts', p as ReflectionPrompt)
      return inserted
    })

    return success(result)
  },

  /**
   * Get responses for a track record
   */
  async getResponses(trackRecordId: UUID): Promise<ApiResponse<ReflectionResponse[]>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const responses = mockDb.findWhere<ReflectionResponse>(
      'reflectionResponses',
      (r) => r.trackRecordId === trackRecordId && r.userId === userId
    )

    return success(responses)
  },

  /**
   * Submit reflection responses for a track record
   */
  async submitResponses(
    trackRecordId: UUID,
    responses: { promptId: UUID; response: string | number }[]
  ): Promise<ApiResponse<ReflectionResponse[]>> {
    await delay(150)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Validate track record exists
    const trackRecord = mockDb.findById('trackRecords', trackRecordId)
    if (!trackRecord) {
      return error('Track record not found')
    }

    // Check for existing responses
    const existingResponses = mockDb.findWhere<ReflectionResponse>(
      'reflectionResponses',
      (r) => r.trackRecordId === trackRecordId && r.userId === userId
    )

    if (existingResponses.length > 0) {
      return error('Reflections already submitted for this track record')
    }

    // Validate all required prompts are answered
    const prompts = mockDb.getAll<ReflectionPrompt>('reflectionPrompts')
    const requiredPromptIds = prompts.filter((p) => p.isRequired).map((p) => p.id)
    const answeredPromptIds = responses.map((r) => r.promptId)

    for (const requiredId of requiredPromptIds) {
      if (!answeredPromptIds.includes(requiredId)) {
        const prompt = prompts.find((p) => p.id === requiredId)
        return error(`Please answer the required question: "${prompt?.question || 'Unknown'}"`)
      }
    }

    // Create response records
    const savedResponses: ReflectionResponse[] = []
    const now = timestamp()

    for (const response of responses) {
      const prompt = prompts.find((p) => p.id === response.promptId)
      if (!prompt) {
        continue // Skip invalid prompt IDs
      }

      const saved = mockDb.insert<ReflectionResponse>('reflectionResponses', {
        promptId: response.promptId,
        trackRecordId,
        userId,
        response: response.response,
        submittedAt: now,
      } as ReflectionResponse)

      savedResponses.push(saved)
    }

    return success(savedResponses)
  },

  /**
   * Check if reflections have been submitted for a track record
   */
  async hasSubmittedReflections(trackRecordId: UUID): Promise<ApiResponse<boolean>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return success(false)
    }

    const responses = mockDb.findWhere<ReflectionResponse>(
      'reflectionResponses',
      (r) => r.trackRecordId === trackRecordId && r.userId === userId
    )

    return success(responses.length > 0)
  },

  /**
   * Get all reflections for the current user
   */
  async getUserReflections(): Promise<ApiResponse<ReflectionResponse[]>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) throw new Error('Not authenticated')

      const responses = mockDb.findWhere<ReflectionResponse>(
        'reflectionResponses',
        (r) => r.userId === userId
      )
      responses.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
      return responses
    })
  },

  /**
   * Get reflection analytics for a class or school
   */
  async getReflectionAnalytics(_scope: 'class' | 'school', _scopeId?: UUID): Promise<ApiResponse<ReflectionAnalytics[]>> {
    return safeExecute(() => {
      const allResponses = mockDb.getAll<ReflectionResponse>('reflectionResponses')
      const prompts = mockDb.getAll<ReflectionPrompt>('reflectionPrompts')

      return prompts.map((prompt) => {
        const responses = allResponses.filter((r) => r.promptId === prompt.id)
        const distribution = responses.reduce<Record<string, number>>((acc, r) => {
          const key = String(r.response)
          acc[key] = (acc[key] || 0) + 1
          return acc
        }, {})

        let averageNumeric: number | undefined
        if (prompt.type === 'numeric_scale' || prompt.type === 'emoji_scale') {
          const nums = responses.map((r) => Number(r.response)).filter((n) => !isNaN(n))
          if (nums.length > 0) {
            averageNumeric = nums.reduce((a, b) => a + b, 0) / nums.length
          }
        }

        return {
          promptId: prompt.id,
          question: prompt.question,
          type: prompt.type,
          selCompetency: prompt.selCompetency,
          responseCount: responses.length,
          distribution,
          averageNumeric,
        } satisfies ReflectionAnalytics
      })
    })
  },

  /**
   * Get the question bank for a school
   */
  async getQuestionBank(schoolId: UUID): Promise<ApiResponse<SchoolQuestionBankPrompt[]>> {
    return safeExecute(() => {
      const prompts = mockDb.findWhere<SchoolQuestionBankPrompt>(
        'questionBankPrompts',
        (p) => p.schoolId === schoolId
      )
      prompts.sort((a, b) => a.displayOrder - b.displayOrder)
      return prompts
    })
  },

  /**
   * Create a custom prompt in the school question bank
   */
  async createPrompt(prompt: Omit<SchoolQuestionBankPrompt, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<SchoolQuestionBankPrompt>> {
    return safeExecute(() => {
      return mockDb.insert<SchoolQuestionBankPrompt>('questionBankPrompts', prompt as SchoolQuestionBankPrompt)
    })
  },

  /**
   * Update a custom prompt
   */
  async updatePrompt(promptId: UUID, updates: Partial<SchoolQuestionBankPrompt>): Promise<ApiResponse<SchoolQuestionBankPrompt>> {
    return safeExecute(() => {
      const updated = mockDb.update<SchoolQuestionBankPrompt>('questionBankPrompts', promptId, updates as Partial<SchoolQuestionBankPrompt>)
      if (!updated) throw new Error('Prompt not found')
      return updated
    })
  },

  /**
   * Delete a custom prompt
   */
  async deletePrompt(promptId: UUID): Promise<ApiResponse<boolean>> {
    return safeExecute(() => {
      return mockDb.delete('questionBankPrompts', promptId)
    })
  },

  /**
   * Get age-tier reflection defaults for a school
   */
  async getReflectionDefaults(_schoolId?: UUID): Promise<ApiResponse<Record<string, {
    enabledPromptTypes: string[]
    maxPromptCount: number
    defaultDifficulty: string
    minResponseLength: number
  }>>> {
    await delay(300)
    // Return stored defaults or platform defaults
    const stored = localStorage.getItem('docurious-reflection-defaults')
    if (stored) return success(JSON.parse(stored))
    return success({
      ages_6_8: { enabledPromptTypes: ['yes_no', 'emoji_scale', 'numeric_scale'], maxPromptCount: 2, defaultDifficulty: 'simple', minResponseLength: 20 },
      ages_9_12: { enabledPromptTypes: ['open_text_short', 'numeric_scale', 'multiple_choice_single', 'yes_no', 'emoji_scale', 'multiple_choice_multi'], maxPromptCount: 3, defaultDifficulty: 'standard', minResponseLength: 50 },
      ages_13_17: { enabledPromptTypes: ['open_text_short', 'open_text_long', 'numeric_scale', 'multiple_choice_single', 'yes_no', 'emoji_scale', 'multiple_choice_multi'], maxPromptCount: 5, defaultDifficulty: 'advanced', minResponseLength: 100 },
    })
  },

  /**
   * Save age-tier reflection defaults for a school
   */
  async saveReflectionDefaults(defaults: Record<string, unknown>): Promise<ApiResponse<boolean>> {
    await delay(500)
    localStorage.setItem('docurious-reflection-defaults', JSON.stringify(defaults))
    return success(true)
  },

  /**
   * Get teacher's reflection config for an assignment
   */
  async getTeacherReflectionConfig(assignmentId: UUID): Promise<ApiResponse<{
    mode: string
    prompts: { id: string; type: string; text: string; required: boolean; options: { id: string; text: string }[] }[]
    selCompetencies: string[]
  } | null>> {
    await delay(300)
    const stored = localStorage.getItem(`docurious-teacher-config-${assignmentId}`)
    if (stored) return success(JSON.parse(stored))
    return success(null)
  },

  /**
   * Save teacher's reflection config for an assignment
   */
  async saveTeacherReflectionConfig(assignmentId: UUID, config: {
    mode: string
    prompts: { id: string; type: string; text: string; required: boolean; options: { id: string; text: string }[] }[]
    selCompetencies: string[]
  }): Promise<ApiResponse<boolean>> {
    await delay(500)
    localStorage.setItem(`docurious-teacher-config-${assignmentId}`, JSON.stringify(config))
    return success(true)
  },
}
